// Intercept API — the dashboard's "Connected Consumers" list
//
// Pure reducer for the 'tunnel-consumer-event' socket event, kept out of App.jsx so it
// can be tested. Every viewer connection has an id (the relay's connection id), sent on
// both "joined" and "left". The list used to drop its LAST row whenever anyone left, so
// after the first viewer of two disconnected it showed the wrong one (B-69).

const MAX_CONSUMERS = 100;

/**
 * @param {Array<{id: string|null, ip: string, joinedAt: number}>} list
 * @param {{event: 'joined'|'left', consumerId?: string, consumerIp?: string, consumerCount?: number}} event
 * @param {number} [now]
 */
export function applyConsumerEvent(list, event, now = Date.now()) {
  if (!event) return list;
  const count = Number.isInteger(event.consumerCount) && event.consumerCount >= 0 ? event.consumerCount : null;
  let next = list;

  if (event.event === 'joined') {
    if (event.consumerId && list.some((c) => c.id === event.consumerId)) return list; // duplicate event
    next = [...list, { id: event.consumerId || null, ip: event.consumerIp || '', joinedAt: now }];
  } else if (event.event === 'left') {
    const index = event.consumerId ? list.findIndex((c) => c.id === event.consumerId) : -1;
    if (index !== -1) {
      next = list.filter((_, i) => i !== index);
    } else if (count === null) {
      // Older extension/relay without ids and without a count: nothing better to go on
      next = list.slice(0, -1);
    }
  } else {
    return list;
  }

  // The relay's count is the truth; without an id to remove (older versions), or after
  // a missed event, drop the oldest rows until the list is no longer than it.
  if (count !== null && next.length > count) next = next.slice(next.length - count);
  return next.length > MAX_CONSUMERS ? next.slice(-MAX_CONSUMERS) : next;
}

const MAX_ACTIVITY = 200;

/**
 * Merge tunnel activity rows (live socket events and rows loaded from the API) into one
 * newest-first list without duplicates. Rows are the tunnel_logs shape: id, method,
 * url, status_code, duration_ms, error, created_at.
 */
export function mergeTunnelActivity(current, incoming) {
  const byId = new Map();
  const noId = [];
  for (const row of [...(incoming || []), ...current]) {
    if (!row) continue;
    if (row.id == null) noId.push(row);
    else if (!byId.has(row.id)) byId.set(row.id, row);
  }
  const time = (row) => new Date(row.created_at || 0).getTime() || 0;
  return [...byId.values(), ...noId].sort((a, b) => time(b) - time(a)).slice(0, MAX_ACTIVITY);
}
